import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '../context/LanguageContext';
import { Map, Layers } from 'lucide-react';

/* ── Tile layers ──────────────────────────────────────────────── */
const TILES = {
  plan: { url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', attr: '© CARTO, © OSM' },
  satellite: { url: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', attr: '© Google' },
};

/* ── Theme ────────────────────────────────────────────────────── */
const GOLD = '#c4a76c';
const DARK = '#0f1f15';
const GREEN_ACCENT = '#10b981';

const SPECIAL_KEYWORDS = ['EV', 'VERT', 'CULTE', 'SCOLAIRE', 'ASSAINISSEMENT', 'SPORT', 'SANTE', 'RUISSEAU'];

/* ── Style per feature ────────────────────────────────────────── */
function getFeatureStyle(feature) {
  const num = String(feature.properties.numero_lot || '').toUpperCase();
  if (num.includes('EV') || num.includes('VERT') || num.includes('RUISSEAU')) {
    return { fillColor: '#2a7d5f', color: GOLD, weight: 1, fillOpacity: 0.45 };
  }
  if (SPECIAL_KEYWORDS.some(k => num.includes(k))) {
    return { fillColor: '#1a3a2a', color: GOLD, weight: 1, fillOpacity: 0.55 };
  }
  return { fillColor: '#3d6b56', color: 'rgba(196,167,108,0.4)', weight: 0.8, fillOpacity: 0.3 };
}

function getHoverStyle(feature) {
  const num = String(feature.properties.numero_lot || '').toUpperCase();
  if (num.includes('EV') || num.includes('VERT') || num.includes('RUISSEAU')) {
    return { fillOpacity: 0.7, weight: 2, color: '#ffffff' };
  }
  if (SPECIAL_KEYWORDS.some(k => num.includes(k))) {
    return { fillOpacity: 0.75, weight: 2, color: '#ffffff' };
  }
  return { fillOpacity: 0.55, weight: 2, color: '#ffffff' };
}

/* ── Popup HTML ───────────────────────────────────────────────── */
function buildPopup(props, language) {
  const num = String(props.numero_lot || 'Inconnu').toUpperCase();
  const ilot = props.ilot || 'N/A';
  const surface = props.superficie || 'N/A';
  const isSpecial = SPECIAL_KEYWORDS.some(k => num.includes(k));

  let header;
  if (isSpecial) {
    header = `<div style="text-align:center;margin-bottom:8px;">
      <span style="font-size:16px;font-weight:700;color:${GREEN_ACCENT};">${num}</span><br/>
      <span style="font-size:12px;color:rgba(255,255,255,0.5);">${language === 'fr' ? 'Îlot' : 'Block'} ${ilot}</span>
    </div>`;
  } else {
    header = `<div style="margin-bottom:8px;">
      <span style="font-size:10px;font-weight:600;color:${GOLD};text-transform:uppercase;letter-spacing:1.5px;">${language === 'fr' ? 'ÎLOT' : 'BLOCK'} ${ilot}</span>
      <h3 style="font-family:'Playfair Display',serif;font-size:18px;font-weight:600;margin:4px 0 0;color:#ffffff;border-bottom:1px solid ${GOLD};padding-bottom:6px;">
        LOT ${props.numero_lot}
      </h3>
    </div>`;
  }

  return `<div style="font-family:'Montserrat',sans-serif;min-width:200px;padding:14px 16px;background:${DARK};color:#e0ebe4;border-radius:8px;">
    ${header}
    <div style="font-size:12px;color:rgba(255,255,255,0.7);display:flex;justify-content:space-between;align-items:center;">
      <span>${language === 'fr' ? 'Surface' : 'Area'}</span>
      <span style="font-weight:600;color:#ffffff;">${surface}</span>
    </div>
  </div>`;
}

/* ── Legend data ───────────────────────────────────────────────── */
const LEGEND_ITEMS = [
  { label_fr: 'Lots résidentiels', label_en: 'Residential lots', color: '#3d6b56' },
  { label_fr: 'Espaces verts', label_en: 'Green spaces', color: '#2a7d5f' },
  { label_fr: 'Équipements', label_en: 'Facilities', color: '#1a3a2a' },
];

/* ══════════════════════════════════════════════════════════════ */
const SongonMapTF753 = () => {
  const { language } = useLanguage();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const geojsonRef = useRef(null);
  const tileRefs = useRef({});
  const [mode, setMode] = useState('plan');
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, ilots: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [mapActive, setMapActive] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef(null);

  /* Detect mobile */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* Lazy-load via IntersectionObserver */
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setIsVisible(true); },
      { threshold: 0.05, rootMargin: '200px' }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  /* Initialise Leaflet */
  useEffect(() => {
    if (!isVisible || !mapRef.current || mapInstance.current) return;

    const planTile = L.tileLayer(TILES.plan.url, { maxZoom: 19, attribution: TILES.plan.attr });
    const satTile = L.tileLayer(TILES.satellite.url, { maxZoom: 19, attribution: TILES.satellite.attr });
    tileRefs.current = { plan: planTile, satellite: satTile };

    const map = L.map(mapRef.current, {
      center: [5.328, -4.262],
      zoom: 17,
      layers: [planTile],
      zoomControl: true,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: !isMobile,
      tap: !isMobile,
      preferCanvas: true,
    });

    mapInstance.current = map;

    fetch('/data/cartemap.geojson')
      .then(r => r.json())
      .then(data => {
        const ilots = new Set();
        data.features.forEach(f => ilots.add(f.properties.ilot));
        setStats({ total: data.features.length, ilots: ilots.size });

        geojsonRef.current = L.geoJSON(data, {
          style: getFeatureStyle,
          onEachFeature: (feature, layer) => {
            const p = feature.properties;
            const num = String(p.numero_lot || 'Inconnu').toUpperCase();
            const ilot = p.ilot || 'N/A';
            const isSpecial = SPECIAL_KEYWORDS.some(k => num.includes(k));
            const tooltipLabel = isSpecial ? `${ilot} | ${num}` : `Îlot ${ilot} | Lot ${p.numero_lot}`;

            layer.bindPopup(buildPopup(p, language), { maxWidth: 280, className: 'ogd-popup' });
            layer.bindTooltip(tooltipLabel, { sticky: true, className: 'ogd-tooltip' });

            layer.on('mouseover', function () { this.setStyle(getHoverStyle(feature)); this.bringToFront(); });
            layer.on('mouseout', function () { geojsonRef.current.resetStyle(this); });
          },
        }).addTo(map);

        map.fitBounds(geojsonRef.current.getBounds(), { padding: [20, 20] });
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));

    return () => { map.remove(); mapInstance.current = null; };
  }, [isVisible, language]);

  /* Toggle plan / satellite */
  const toggleMode = () => {
    if (!mapInstance.current) return;
    const next = mode === 'plan' ? 'satellite' : 'plan';
    mapInstance.current.removeLayer(tileRefs.current[mode]);
    mapInstance.current.addLayer(tileRefs.current[next]);
    setMode(next);
  };

  /* Activate map interactions (mobile overlay tap / desktop click) */
  const activateMap = () => {
    if (!mapInstance.current) return;
    mapInstance.current.dragging.enable();
    mapInstance.current.scrollWheelZoom.enable();
    if (mapInstance.current.tap) mapInstance.current.tap.enable();
    setMapActive(true);
  };

  const deactivateMap = () => {
    if (!mapInstance.current) return;
    if (isMobile) {
      mapInstance.current.dragging.disable();
      if (mapInstance.current.tap) mapInstance.current.tap.disable();
    }
    mapInstance.current.scrollWheelZoom.disable();
    setMapActive(false);
  };

  return (
    <section id="masterplan" ref={sectionRef} className="relative w-full bg-stone-950" data-testid="songon-map-section">
      {/* Header */}
      <div className="bg-stone-950 py-10 lg:py-14 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <span className="text-xs font-medium tracking-[0.2em] uppercase" style={{ color: GOLD, fontFamily: "'Montserrat', sans-serif" }}>
                {language === 'fr' ? 'Plan de Masse Officiel' : 'Official Masterplan'}
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Songon East-Side
              </h2>
              <p className="mt-3 text-white/50 text-sm sm:text-base max-w-xl" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {language === 'fr'
                  ? `${stats.total} parcelles réparties sur ${stats.ilots} îlots — cliquez pour explorer`
                  : `${stats.total} plots across ${stats.ilots} blocks — click to explore`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Legend */}
              <div className="hidden sm:flex items-center gap-4 mr-4">
                {LEGEND_ITEMS.map(item => (
                  <div key={item.color} className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm" style={{ background: item.color, border: `1px solid ${GOLD}40` }} />
                    <span className="text-white/60 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>{language === 'fr' ? item.label_fr : item.label_en}</span>
                  </div>
                ))}
              </div>
              {/* Toggle */}
              <button onClick={toggleMode} data-testid="map-toggle-btn"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-semibold tracking-wider uppercase transition-all"
                style={{ background: mode === 'satellite' ? GREEN_ACCENT : '#1a1a1a', color: '#ffffff', border: `1px solid ${GOLD}`, fontFamily: "'Montserrat', sans-serif" }}>
                <Layers className="w-4 h-4" />
                {mode === 'plan'
                  ? (language === 'fr' ? 'Vue Satellite' : 'Satellite View')
                  : (language === 'fr' ? 'Vue Plan' : 'Plan View')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="relative w-full h-[55vh] sm:h-[65vh] lg:h-[75vh]" style={{ touchAction: mapActive ? 'none' : 'pan-y' }}>
        {isLoading && isVisible && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-stone-950">
            <div className="text-center">
              <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: `${GREEN_ACCENT} transparent ${GREEN_ACCENT} ${GREEN_ACCENT}` }} />
              <p className="text-white/50 text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {language === 'fr' ? 'Chargement du plan de masse...' : 'Loading masterplan...'}
              </p>
            </div>
          </div>
        )}

        {isVisible && <div ref={mapRef} className="w-full h-full" style={{ background: '#0a1a0f' }} data-testid="leaflet-map-tf753" />}

        {!isVisible && (
          <div className="w-full h-full bg-stone-900 flex items-center justify-center">
            <Map className="w-12 h-12 mx-auto mb-3" style={{ color: 'rgba(16,185,129,0.3)' }} />
          </div>
        )}

        {/* Mobile touch overlay — blocks map drag, allows page scroll */}
        {isMobile && !mapActive && !isLoading && isVisible && (
          <div className="absolute inset-0 z-[1001] flex items-center justify-center" data-testid="map-touch-overlay"
            onClick={activateMap} style={{ background: 'rgba(0,0,0,0.15)' }}>
            <div className="bg-black/60 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
              <p className="text-white text-xs font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {language === 'fr' ? 'Touchez pour explorer la carte' : 'Tap to explore the map'}
              </p>
            </div>
          </div>
        )}

        {/* Deactivate button (mobile only, when map is active) */}
        {isMobile && mapActive && (
          <button onClick={deactivateMap} data-testid="map-deactivate-btn"
            className="absolute top-3 right-3 z-[1002] px-4 py-2 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-all"
            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', color: GOLD, border: `1px solid ${GOLD}50`, fontFamily: "'Montserrat', sans-serif" }}>
            {language === 'fr' ? 'Fermer la carte' : 'Close map'}
          </button>
        )}

        {/* Mobile legend */}
        {isMobile && (
          <div className="absolute bottom-4 left-4 z-[1000] bg-stone-900/90 backdrop-blur-sm rounded-lg p-3 border border-white/10">
            {LEGEND_ITEMS.map(item => (
              <div key={item.color} className="flex items-center gap-2 py-1">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: item.color }} />
                <span className="text-white/70 text-[10px]">{language === 'fr' ? item.label_fr : item.label_en}</span>
              </div>
            ))}
          </div>
        )}

        {/* Desktop scroll hint */}
        {!isMobile && !isLoading && !mapActive && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full cursor-pointer" onClick={activateMap}>
            <p className="text-white/60 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {language === 'fr' ? 'Cliquez pour activer le zoom' : 'Click to enable zoom'}
            </p>
          </div>
        )}
      </div>

      {/* Leaflet custom styles */}
      <style>{`
        .ogd-popup .leaflet-popup-content-wrapper {
          background: transparent !important;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4) !important;
          border-radius: 8px !important;
          padding: 0 !important;
          border: 1px solid ${GOLD}30 !important;
        }
        .ogd-popup .leaflet-popup-content { margin: 0 !important; }
        .ogd-popup .leaflet-popup-tip { background: ${DARK} !important; border: 1px solid ${GOLD}30 !important; }
        .ogd-tooltip {
          background: rgba(15,31,21,0.92) !important;
          color: #ffffff !important;
          border: 1px solid ${GOLD} !important;
          font-weight: 600 !important;
          font-size: 11px !important;
          font-family: 'Montserrat', sans-serif !important;
          padding: 6px 12px !important;
          border-radius: 6px !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important;
        }
        .ogd-tooltip::before { border-top-color: ${GOLD} !important; }
        .leaflet-control-zoom a {
          background: ${DARK} !important;
          color: ${GOLD} !important;
          border-color: ${GOLD}40 !important;
        }
        .leaflet-control-zoom a:hover { background: #1a3a2a !important; color: #ffffff !important; }
      `}</style>
    </section>
  );
};

export default SongonMapTF753;
